import { Label } from "@measured/puck";
import React, { useEffect, useState } from "react";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import { LucideProps } from "lucide-react";
import dynamicIconImports from "lucide-react/dynamicIconImports";

// Curated list of Lucide icons for graph/data blocks
const graphIcons = [
  { label: "Bar Chart", value: "bar-chart" },
  { label: "Line Chart", value: "line-chart" },
  { label: "Pie Chart", value: "pie-chart" },
  { label: "Trending Up", value: "trending-up" },
  { label: "Activity", value: "activity" },
  { label: "Layers", value: "layers" },
  { label: "Database", value: "database" },
  { label: "Percent", value: "percent" },
  { label: "Circle", value: "circle" },
  { label: "Grid", value: "layout-grid" },
  { label: "Scatter Chart", value: "scatter-chart" },
  { label: "Area Chart", value: "area-chart" },
  { label: "Calendar", value: "calendar" },
  { label: "Clock", value: "clock" },
  { label: "Star", value: "star" },
  { label: "Chevron Up", value: "chevron-up" },
  { label: "Chevron Down", value: "chevron-down" },
  { label: "Chevron Left", value: "chevron-left" },
  { label: "Chevron Right", value: "chevron-right" },
  { label: "Arrow Up Right", value: "arrow-up-right" },
  { label: "Arrow Down Left", value: "arrow-down-left" },
  { label: "Bar Chart 2", value: "bar-chart-2" },
  { label: "Pie Chart 2", value: "pie-chart-2" },
  { label: "Layout Dashboard", value: "layout-dashboard" },
  { label: "Plus", value: "plus" },
];

const iconOptions = [{ label: "None", value: "none" }, ...graphIcons];

type DynamicIconProps = {
  name: string;
  size?: number;
  color?: string;
};

export const DynamicLucideIcon: React.FC<DynamicIconProps> = ({
  name,
  size = 18,
  color = "currentColor",
}) => {
  const [Icon, setIcon] = useState<any>(null);

  useEffect(() => {
    let mounted = true;
    const loadIcon = async () => {
      if (name === "none") {
        setIcon(null);
        return;
      }
      const iconImport =
        dynamicIconImports[name as keyof typeof dynamicIconImports];
      if (iconImport) {
        const icon = await iconImport();
        if (mounted) setIcon(() => icon.default);
      }
    };
    loadIcon();
    return () => {
      mounted = false;
    };
  }, [name]);

  if (!Icon) return null;
  return <Icon size={size} color={color} />;
};

export interface BlockIconSelectorProps {
  value?: string;
  onChange?: (value: string) => void;
}

const BlockIconSelector: React.FC<BlockIconSelectorProps> = ({
  value,
  onChange,
}) => {
  const selected = iconOptions.find((opt) => opt.value === value);
  return (
    <div className="flex flex-col gap-2 w-2/5">
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="w-full">
          <span className="flex items-center gap-2">
            <SelectValue placeholder="Select an icon">
              {value !== "none" ? (
                <span className="flex items-center gap-2">
                  <DynamicLucideIcon name={value || "none"} size={18} />
                  {selected?.label}
                </span>
              ) : null}
            </SelectValue>
          </span>
        </SelectTrigger>
        <SelectContent className="max-h-80 overflow-y-auto">
          {iconOptions.map((icon) => (
            <SelectItem
              key={icon.value}
              value={icon.value}
              className="flex items-center gap-2"
            >
              <DynamicLucideIcon name={icon.value} size={16} />
              {icon.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default BlockIconSelector;
